import { t as uiText } from '../i18n';
import React from 'react';

interface FileDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedFileCount: number;
  hasReferencedFiles: boolean;
  isDeleting?: boolean;
}

const FileDeleteModal: React.FC<FileDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedFileCount,
  hasReferencedFiles,
  isDeleting = false
}) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '480px',
        width: '90%',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        position: 'relative'
      }}>
        {/* ヘッダー */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            flex: 1
          }}>
            {uiText("Confirm file deletion")}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              color: '#666',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>

        {/* 警告アイコンとメッセージ */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            fontSize: '24px',
            color: '#f56565',
            flexShrink: 0,
            marginTop: '2px'
          }}>
            ⚠️
          </div>
          <div>
            <div style={{
              fontSize: '16px',
              color: '#333',
              marginBottom: '8px',
              lineHeight: '1.5'
            }}>
              {selectedFileCount === 1 
                ? uiText("You are about to delete the selected files.") 
                : uiText("You are about to delete {{selectedFileCount}} selected files.", { selectedFileCount: selectedFileCount })}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.5',
              marginBottom: '8px'
            }}>
              <strong>{uiText("Once deleted, this cannot be undone.")}</strong>
            </div>
            {hasReferencedFiles && (
              <div style={{
                fontSize: '14px',
                color: '#e53e3e',
                lineHeight: '1.5',
                backgroundColor: '#fed7d7',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #feb2b2'
              }}>
                {uiText("If other pages reference this, files in those pages are also deleted.")}
              </div>
            )}
          </div>
        </div>

        {/* 削除中の進行状況表示 */}
        {isDeleting && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#fef3c7',
            borderRadius: '4px',
            border: '1px solid #f59e0b'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid #e0e0e0',
              borderTopColor: 'var(--workspace-primary, #cfae91)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ color: '#92400e', fontSize: '14px' }}>
              {uiText("Deleting file...")}
            </span>
          </div>
        )}

        {/* ボタンエリア */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={isDeleting}
            style={{
              padding: '8px 16px',
              backgroundColor: '#fff',
              color: isDeleting ? '#9ca3af' : '#666',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              opacity: isDeleting ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#9ca3af';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.borderColor = '#d1d5db';
              }
            }}
          >
            {uiText("Cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            style={{
              padding: '8px 16px',
              backgroundColor: isDeleting ? '#9ca3af' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s ease',
              opacity: isDeleting ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.backgroundColor = '#b91c1c';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.backgroundColor = '#dc2626';
              }
            }}
          >
            {isDeleting ? uiText("Deleting...") : uiText("Delete")}
          </button>
        </div>

        {/* スピンアニメーション用のCSS */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default FileDeleteModal;
